import { useState, useEffect, useRef } from "react";
import QRCode from "react-qr-code";
import copy from "clipboard-copy";
import { Button } from "@/components/ui/button";
import { Loader2, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Mask } from "@/utils/mask";
import { checkPaymentStatus } from "@/Api/sales";
import { PIX_STATUS } from "@/utils/constants";

interface TransactionPixDetail {
  id: number;
  payer_name?: string | null;
  payer_document?: string | null;
  payer_document_type?: string | null;
}

interface PixPaymentProps {
  transaction: {
    id: number;
    uuid: string;
    id_transaction: string;
    emv?: string | null;
    pix_copy_and_paste?: string;
    pix_key: string;
    amount: string;
    url_qr_code: string;
    status: string;
    transaction_pix_details?: TransactionPixDetail[];
  };
  saleId?: number;
  onPaymentConfirmed: () => void;
  onClose: () => void;
}

export function PixPayment({
  transaction,
  onPaymentConfirmed,
  onClose,
  saleId,
}: PixPaymentProps) {
  const [copied, setCopied] = useState(false);
  const [checking, setChecking] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(
    String(transaction?.status || "")
  );
  const [payerNames, setPayerNames] = useState<string[]>(() => {
    const names = transaction?.transaction_pix_details
      ?.map((detail) => detail.payer_name)
      .filter((name): name is string => !!name) || [];
    return names;
  });
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  const handleCopyPix = async () => {
    try {
      await copy(transaction?.pix_copy_and_paste || "");
      setCopied(true);
      toast.success("Código PIX copiado!");
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error("Erro ao copiar código PIX");
    }
  };

  const checkStatus = async (skipStatusCheck = false) => {
    try {
      const alreadyPaid = transaction?.status === PIX_STATUS.PAID || paymentStatus === PIX_STATUS.PAID;
      if (alreadyPaid && !skipStatusCheck) return;

      setChecking(true);
      const { data, ok } = await checkPaymentStatus(String(saleId));
      if (!ok) return;

      // A resposta agora é um array de transações
      const transactions = data?.response || [];

      // Encontra a transação correspondente pela uuid ou id
      const currentTransaction = transactions.find(
        (t: any) => t.uuid === transaction.uuid || t.id === transaction.id
      );

      if (!currentTransaction) return;

      const status = currentTransaction.status;
      setPaymentStatus(status);

      // Extrai nomes dos pagadores do transaction_pix_details
      if (currentTransaction.transaction_pix_details?.length > 0) {
        const names = currentTransaction.transaction_pix_details
          .map((detail: TransactionPixDetail) => detail.payer_name)
          .filter((name: string | null | undefined): name is string => !!name);
        if (names.length > 0) {
          setPayerNames(names);
        }
      }

      if (status === PIX_STATUS.PAID && !alreadyPaid) {
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
        onPaymentConfirmed();
        toast.success("Pagamento confirmado!");
      }
    } catch (error) {
      console.error("Erro ao verificar status do pagamento:", error);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    const isPaid = transaction?.status === PIX_STATUS.PAID;

    // Se já está pago, busca detalhes do pagador sem verificar status novamente
    if (isPaid) {
      checkStatus(true);
    } else {
      checkStatus();
      const id = setInterval(checkStatus, 10000);
      intervalIdRef.current = id;
    }

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, []);

  const isPaid = paymentStatus === PIX_STATUS.PAID;

  return (
    <div className="flex flex-col items-center p-6 bg-white dark:bg-zinc-800 rounded-lg shadow-sm">
      <h3 className="text-lg font-semibold mb-4">Pagamento via PIX</h3>

      {isPaid ? (
        <div className="flex flex-col items-center justify-center py-8">
          <div className="bg-green-100 p-4 rounded-full mb-4">
            <CheckCircle2 className="w-16 h-16 text-green-600" />
          </div>
          <h4 className="text-xl font-bold text-green-600 mb-2">
            Pagamento Confirmado!
          </h4>
          {payerNames.length > 0 && (
            <div className="text-center mb-2">
              <p className="text-sm text-gray-500">Nome do Pagador</p>
              <p className="font-semibold text-gray-800">
                {payerNames.join(", ")}
              </p>
            </div>
          )}
          <p className="text-center text-gray-600 mb-4">
            O pagamento de {Mask.formatCurrency(parseFloat(transaction.amount))}{" "}
            foi confirmado com sucesso.
          </p>
          <Button variant="outline" className="mt-4" onClick={() => onClose()}>
            Fechar
          </Button>
        </div>
      ) : (
        <>
          <div className="bg-white p-4 rounded-lg mb-4">
            <QRCode
              value={transaction?.pix_copy_and_paste || ""}
              size={192}
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
            />
          </div>

          <div className="w-full max-w-xs mb-4">
            <p className="text-sm text-center mb-2">Valor a pagar:</p>
            <p className="text-2xl font-bold text-center text-green-600 mb-4">
              {Mask.formatCurrency(parseFloat(transaction.amount))}
            </p>

            <div className="flex items-center gap-2 p-2 bg-gray-100 dark:bg-zinc-700 rounded-md">
              <p className="text-xs flex-1 truncate">
                {transaction?.pix_copy_and_paste}
              </p>
              <Button
                size="icon"
                onClick={handleCopyPix}
                className="h-8 w-8 shrink-0"
              >
                {copied ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <div className="w-full max-w-xs">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => checkStatus()}
              disabled={checking}
            >
              {checking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verificando...
                </>
              ) : (
                "Verificar pagamento"
              )}
            </Button>

            <p className="text-xs text-center mt-2 text-muted-foreground">
              O status do pagamento é verificado automaticamente a cada 10
              segundos.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
